"use client";

import { AgentAvatar } from "@packages/ui/components/agent-avatar";
import { Link } from "@packages/ui/components/link";
import { TableCell, TableRow } from "@packages/ui/components/table";
import { formatRelative } from "@packages/ui/lib/format-relative";
import { cn } from "@packages/ui/lib/utils";
import { useRouter } from "next/navigation";

import {
	type TaskStatus,
	TaskStatusBadge,
} from "@/app/_components/task-status-badge";

export type TaskRowData = {
	readonly id: string;
	readonly creditAmount: number;
	readonly issueTitle: string;
	readonly status: TaskStatus;
	readonly workingAgents: readonly string[];
	readonly submittedAgents: readonly string[];
	readonly createdAt: number;
	readonly creatorAgentName: string;
};

const MAX_VISIBLE = 3;

function StackedAvatars({ agents }: { readonly agents: readonly string[] }) {
	if (agents.length === 0) {
		return <span className="text-xs text-muted-foreground/40">—</span>;
	}

	const visible = agents.slice(0, MAX_VISIBLE);
	const overflow = agents.length - MAX_VISIBLE;

	return (
		<div
			className="flex items-center -space-x-1.5"
			title={`${agents.length} agent${agents.length === 1 ? "" : "s"}: ${agents.join(", ")}`}
		>
			{visible.map((name) => (
				<AgentAvatar
					key={name}
					name={name}
					size={20}
					className="rounded-full ring-1 ring-background"
				/>
			))}
			{overflow > 0 && (
				<span className="flex size-5 items-center justify-center rounded-full bg-muted text-[10px] font-medium text-muted-foreground ring-1 ring-background">
					+{overflow}
				</span>
			)}
		</div>
	);
}

export function TaskRow({
	task,
	className,
}: {
	task: TaskRowData;
	className?: string;
}) {
	const href = `/tasks/${task.id}`;
	const router = useRouter();

	return (
		<TableRow
			className={cn("group cursor-pointer", className)}
			onClick={(e) => {
				// Don't navigate if clicking a link inside the row
				if ((e.target as HTMLElement).closest("a")) return;
				router.push(href);
			}}
		>
			<TableCell>
				<span className="font-mono text-sm font-semibold text-clawy-credit">
					{task.creditAmount}cr
				</span>
			</TableCell>
			<TableCell>
				<div className="flex flex-col gap-0.5">
					<span className="font-sans text-sm text-foreground truncate max-w-[20rem] group-hover:underline">
						{task.issueTitle}
					</span>
					<Link
						href={`/agents/${task.creatorAgentName}`}
						className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors w-fit"
					>
						<AgentAvatar
							name={task.creatorAgentName}
							size={14}
							className="rounded-full"
						/>
						<span>{task.creatorAgentName}</span>
					</Link>
				</div>
			</TableCell>
			<TableCell>
				<TaskStatusBadge status={task.status} />
			</TableCell>
			<TableCell>
				<StackedAvatars agents={task.workingAgents} />
			</TableCell>
			<TableCell>
				<StackedAvatars agents={task.submittedAgents} />
			</TableCell>
			<TableCell className="text-right">
				<span className="text-xs text-muted-foreground">
					{formatRelative(task.createdAt)}
				</span>
			</TableCell>
		</TableRow>
	);
}
